import { useState } from "react";
import { useNavigate } from "react-router";
import type { FallbackProps } from "react-error-boundary";
import { AlertTriangle, ChevronDown, ChevronUp, RotateCcw, LayoutDashboard } from "lucide-react";

export function PageErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const [showDetails, setShowDetails] = useState(false);
  const navigate = useNavigate();
  const err = error instanceof Error ? error : new Error(String(error));

  return (
    <div className="flex items-center justify-center min-h-[60vh] px-4">
      <div className="w-full max-w-md rounded-xl bg-[var(--color-surface-1)] shadow-lg shadow-black/25 p-6 space-y-5">
        {/* Icon + Title */}
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
            <AlertTriangle size={22} className="text-amber-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-zinc-100 tracking-tight">Something went wrong</h2>
            <p className="text-sm text-zinc-500 mt-1">
              {err.message || "An unexpected error occurred while rendering this page."}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={resetErrorBoundary}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500/15 border border-amber-500/25 text-amber-400 text-sm font-bold hover:bg-amber-500/25 transition-colors"
          >
            <RotateCcw size={14} />
            Try Again
          </button>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-zinc-800 border border-white/[0.07] text-zinc-300 text-sm font-bold hover:bg-zinc-700 transition-colors"
          >
            <LayoutDashboard size={14} />
            Dashboard
          </button>
        </div>

        {/* Expandable technical details */}
        <button
          onClick={() => setShowDetails((v) => !v)}
          className="flex items-center gap-1.5 text-xs text-zinc-600 hover:text-zinc-400 transition-colors w-full"
        >
          {showDetails ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          Technical details
        </button>
        {showDetails && (
          <pre className="text-[11px] text-zinc-500 bg-zinc-900/60 rounded-lg p-3 overflow-x-auto max-h-48 overflow-y-auto font-mono leading-relaxed whitespace-pre-wrap break-words border border-white/[0.04]">
            {err.stack || String(error)}
          </pre>
        )}
      </div>
    </div>
  );
}
